import React, { useEffect, useMemo, useState } from 'react';
import { ShoppingCart, X } from 'lucide-react';
import { useAffiliateLink } from '@/hooks/useAffiliateLink';
import { createConversionClickHandler } from '@/utils/conversionTracking';

const COOKIE_CONSENT_KEY = 'cookie_consent';
const DISMISS_KEY = 'floating_cta_dismissed';

/**
 * 移动端底部悬浮 CTA（Affiliate clickout）
 * - 仅移动端显示（md:hidden）
 * - 滚动一定距离后出现
 * - 可关闭（本次会话内不再出现）
 * - 自动使用 useAffiliateLink 替换占位符并用 Google Ads clickout 埋点
 * - z-index 低于 CookieBanner；若未 consent，则上移避免被 CookieBanner 覆盖
 */
export const MobileFloatingCTA = ({
    affiliateLinkTemplate,
    productName,
    badge,
    price,
	    ctaText = 'Zum Angebot',
    scrollThreshold = 120,
}) => {
    const affiliateLink = useAffiliateLink(affiliateLinkTemplate);
    const handleClick = useMemo(
        () => createConversionClickHandler(affiliateLink),
        [affiliateLink]
    );

    const [isShown, setIsShown] = useState(false);
    const [isDismissed, setIsDismissed] = useState(() => {
        if (typeof window === 'undefined') return false;
        return sessionStorage.getItem(DISMISS_KEY) === '1';
    });
    const [consent, setConsent] = useState(() => {
        if (typeof window === 'undefined') return null;
        return localStorage.getItem(COOKIE_CONSENT_KEY);
    });

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const update = () => {
            setConsent(localStorage.getItem(COOKIE_CONSENT_KEY));
        };

        // CookieBanner 内部会派发该事件（我们会在 CookieBanner 里补上）
        window.addEventListener('cookie-consent-changed', update);

        return () => {
            window.removeEventListener('cookie-consent-changed', update);
        };
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const onScroll = () => {
            setIsShown(window.scrollY >= scrollThreshold);
        };

        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });

        return () => window.removeEventListener('scroll', onScroll);
    }, [scrollThreshold]);

    if (!affiliateLink || isDismissed || !isShown) return null;

    // 若用户尚未同意/拒绝 cookie，CookieBanner 会从底部弹出（z-50）。
    // 这里把 CTA 上移一点，避免被 CookieBanner 遮挡。
    const bottomClass = consent ? 'bottom-0' : 'bottom-24';

    const handleDismiss = () => {
        setIsDismissed(true);
        try {
            sessionStorage.setItem(DISMISS_KEY, '1');
        } catch (_) {
            // ignore
        }
    };

    return (
        <div
            className={`md:hidden fixed left-0 right-0 ${bottomClass} z-40 px-3 pb-3`}
            style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        >
            <div className="max-w-3xl mx-auto">
                <div className="relative rounded-2xl border border-border bg-card shadow-elevated">
                    <button
                        type="button"
                        onClick={handleDismiss}
                        className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-card border border-border shadow-md flex items-center justify-center"
                        aria-label="CTA schließen"
                    >
                        <X className="h-4 w-4 text-muted-foreground" />
                    </button>

                    <div className="flex items-center gap-3 p-3">
                        <div className="flex-1 min-w-0">
                            {badge && (
                                <div className="text-[11px] font-semibold text-amber-600 mb-0.5">
                                    {badge}
                                </div>
                            )}
                            <div className="flex items-center gap-2">
                                <div className="truncate text-sm font-semibold text-foreground">
                                    {productName || 'Zum Angebot'}
                                </div>
                                {price && (
                                    <div className="text-sm font-bold text-primary whitespace-nowrap">
                                        {price}
                                    </div>
                                )}
                            </div>
                        </div>

                        <a
                            href={affiliateLink}
                            onClick={handleClick}
                            rel="noopener noreferrer sponsored"
                            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-4 py-3 rounded-xl font-semibold transition-colors shadow-md"
	                        aria-label={ctaText}
                        >
                            <ShoppingCart className="h-5 w-5" />
	                        <span>{ctaText}</span>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};
